import { z } from "zod";
import { ProjectOutline, workstreamDeliverableSchema } from "./project";

export function generateId(): string {
    return crypto.randomUUID();
}

const createWorkstreamChangeSchema = z.object({
    type: z.literal("createWorkstream"),
    name: z.string(),
    description: z.string(),
    deliverables: z.array(workstreamDeliverableSchema),
});

const createDeliverableChangeSchema = z.object({
    type: z.literal("createDeliverable"),
    workstreamId: z.string(),
    name: z.string(),
    icon: z.string(),
    description: z.string(),
    startDate: z.string().date().optional(),
    targetDate: z.string().date().optional(),
});

const deleteWorkstreamChangeSchema = z.object({
    type: z.literal("deleteWorkstream"),
    workstreamId: z.string(),
});

const deleteDeliverableChangeSchema = z.object({
    type: z.literal("deleteDeliverable"),
    workstreamId: z.string(),
    deliverableId: z.string(),
});

const updateWorkstreamChangeSchema = z.object({
    type: z.literal("updateWorkstream"),
    workstreamId: z.string(),
    name: z.string().optional(),
    description: z.string().optional(),
});

const updateDeliverableChangeSchema = z.object({
    type: z.literal("updateDeliverable"),
    workstreamId: z.string(),
    deliverableId: z.string(),
    name: z.string().optional(),
    icon: z.string().optional(),
    description: z.string().optional(),
});

export const projectDiffSchema = z.object({
    id: z.string(),
    changes: z.array(
        z.discriminatedUnion("type", [
            createWorkstreamChangeSchema,
            updateWorkstreamChangeSchema,
            deleteWorkstreamChangeSchema,
            createDeliverableChangeSchema,
            updateDeliverableChangeSchema,
            deleteDeliverableChangeSchema,
        ])
    ),
});

export type ProjectDiff = z.infer<typeof projectDiffSchema>;

export function applyDiffToOutline(outline: ProjectOutline, diff: ProjectDiff): ProjectOutline {
    const newOutline = JSON.parse(JSON.stringify(outline)) as ProjectOutline;

    for (const change of diff.changes) {
        switch (change.type) {
            case "createWorkstream": {
                const newWorkstream = {
                    id: generateId(),
                    name: change.name,
                    description: change.description,
                    deliverables: change.deliverables.map((d) => ({
                        ...d,
                        id: generateId(),
                    })),
                };
                newOutline.workstreams.push(newWorkstream);
                break;
            }

            case "updateWorkstream": {
                const workstream = newOutline.workstreams.find((w) => w.id === change.workstreamId);
                if (workstream) {
                    if (change.name !== undefined) {
                        workstream.name = change.name;
                    }
                    if (change.description !== undefined) {
                        workstream.description = change.description;
                    }
                }
                break;
            }

            case "deleteWorkstream": {
                const index = newOutline.workstreams.findIndex((w) => w.id === change.workstreamId);
                if (index !== -1) {
                    newOutline.workstreams.splice(index, 1);
                }
                break;
            }

            case "createDeliverable": {
                const workstream = newOutline.workstreams.find((w) => w.id === change.workstreamId);
                if (workstream) {
                    const newDeliverable = {
                        id: generateId(),
                        name: change.name,
                        icon: change.icon,
                        description: change.description,
                        startDate: change.startDate,
                        targetDate: change.targetDate,
                    };
                    workstream.deliverables.push(newDeliverable);
                }
                break;
            }

            case "updateDeliverable": {
                const workstream = newOutline.workstreams.find((w) => w.id === change.workstreamId);
                if (workstream) {
                    const deliverable = workstream.deliverables.find((d) => d.id === change.deliverableId);
                    if (deliverable) {
                        if (change.name !== undefined) {
                            deliverable.name = change.name;
                        }
                        if (change.icon !== undefined) {
                            deliverable.icon = change.icon;
                        }
                        if (change.description !== undefined) {
                            deliverable.description = change.description;
                        }
                    }
                }
                break;
            }

            case "deleteDeliverable": {
                const workstream = newOutline.workstreams.find((w) => w.id === change.workstreamId);
                if (workstream) {
                    const index = workstream.deliverables.findIndex((d) => d.id === change.deliverableId);
                    if (index !== -1) {
                        workstream.deliverables.splice(index, 1);
                    }
                }
                break;
            }
        }
    }

    return newOutline;
}

interface DiffHighlighting {
    workstreams: Record<string, "created" | "deleted" | "updated">;
    // deliverables: Record<string, Record<string, "created" | "deleted" | "updated">>;
}

export function computeDiffHighlighting(original: ProjectOutline, diff: ProjectDiff): DiffHighlighting {
    const res: DiffHighlighting = {
        workstreams: {},
    };

    const originalIds = new Set(original.workstreams.map((w) => w.id));
    const result = applyDiffToOutline(original, diff);
    const resultIds = new Set(result.workstreams.map((w) => w.id));

    for (const id of resultIds) {
        if (!originalIds.has(id)) {
            res.workstreams[id] = "created";
        }
    }
    for (const id of originalIds) {
        if (!resultIds.has(id)) {
            res.workstreams[id] = "deleted";
        }
    }

    for (const change of diff.changes) {
        switch (change.type) {
            case "updateWorkstream":
                res.workstreams[change.workstreamId] = "updated";
                break;
            case "createDeliverable":
            case "updateDeliverable":
            case "deleteDeliverable":
                if (!res.workstreams[change.workstreamId]) {
                    res.workstreams[change.workstreamId] = "updated";
                }
                break;
        }
    }

    return res;
}
